<script setup lang="ts">
import { computed } from 'vue';

import KSwitchField from '@/components/fields/KSwitchField.vue';
import { DRAG_HANDLE_CLASS } from '@/composables/jobEdit/useJobEditSectionsOrder';

type StatusTone = 'default' | 'hold' | 'pending' | 'success';

export interface Props {
  paymentStatus?: string;
  invoiceStatus?: string;
  isHoldLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  paymentStatus: '',
  invoiceStatus: '',
  isHoldLoading: false,
});

const emit = defineEmits<{
  toggleHold: [];
}>();

const paymentStatusLabel = computed(() => props.paymentStatus || 'Unknown');
const invoiceStatusLabel = computed(() => props.invoiceStatus || 'Unknown');

const payApproved = computed(
  () =>
    props.paymentStatus.toLowerCase() !== 'on hold' &&
    props.invoiceStatus.toLowerCase() !== 'on hold',
);

function getStatusTone(status: string): StatusTone {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'paid' || normalizedStatus === 'invoiced') {
    return 'success';
  }

  if (normalizedStatus === 'pending') {
    return 'pending';
  }

  if (normalizedStatus === 'on hold') {
    return 'hold';
  }

  return 'default';
}
</script>

<template>
  <section
    :class="$style.jobEditStatusSection"
    data-testid="jobEditStatusSection"
  >
    <div :class="$style.contentRow">
      <div :class="$style.leftGroup">
        <v-icon :class="[DRAG_HANDLE_CLASS, $style.dragHandle]" size="20"
          >mdi-drag-vertical</v-icon
        >
        <h3 :class="$style.title">STATUS</h3>

        <div :class="$style.statusRow">
          <div :class="$style.statusItem">
            <span :class="$style.statusLabel">Payment</span>
            <v-chip
              size="x-small"
              variant="flat"
              :class="[
                $style.statusChip,
                $style[getStatusTone(paymentStatusLabel)],
              ]"
            >
              {{ paymentStatusLabel }}
            </v-chip>
          </div>

          <div :class="$style.statusItem">
            <span :class="$style.statusLabel">Invoice</span>
            <v-chip
              size="x-small"
              variant="flat"
              :class="[
                $style.statusChip,
                $style[getStatusTone(invoiceStatusLabel)],
              ]"
            >
              {{ invoiceStatusLabel }}
            </v-chip>
          </div>
        </div>
      </div>

      <KSwitchField
        :model-value="!payApproved"
        :class="$style.payApprovedSwitch"
        :label="payApproved ? 'Pay Approved' : 'Pay On Hold'"
        :priority="payApproved ? 'success' : 'danger'"
        is-small
        hide-details
        :disabled="props.isHoldLoading"
        @update:model-value="emit('toggleHold')"
      />
    </div>
  </section>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditStatusSection {
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid colors.$color-gray-200;
  background-color: colors.$color-white;
  padding: 4px 16px;
}

.contentRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex-wrap: wrap;
  row-gap: 8px;
}

.leftGroup {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.payApprovedSwitch {
  flex-shrink: 0;
  align-self: center;
  margin-left: auto;
}

.dragHandle {
  color: colors.$color-text-tertiary;
  flex-shrink: 0;
}

.title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: colors.$color-text-secondary;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  flex-shrink: 0;
}

.statusRow {
  display: flex;
  align-items: center;
  margin-top: 0;
  flex-wrap: wrap;
  column-gap: 0;
  row-gap: 6px;
  white-space: normal;
}

.statusItem {
  display: flex;
  align-items: center;
  gap: 8px;

  & + & {
    margin-left: 12px;
    padding-left: 12px;
    border-left: 1px solid colors.$color-gray-300;
  }
}

.statusLabel {
  color: colors.$color-text-primary;
  font-size: 0.75rem;
  font-weight: 600;
}

.statusChip {
  --v-chip-height: 20px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.default {
  background-color: colors.$color-gray-100;
  color: colors.$color-text-secondary;
}

.pending {
  background-color: colors.$color-brand-100;
  color: colors.$color-brand-800;
}

.hold {
  background-color: colors.$color-gray-200;
  color: colors.$color-text-secondary;
}

.success {
  background-color: colors.$color-green-100;
  color: colors.$color-green-800;
}
</style>
